'use strict';
import Search from '@components/Common/Search.js';
import { CheckIcon } from '@icons/index.js';
import FoldersState from '@states/FoldersState.js';
import ProjectsState from '@states/Projects.js';
import { UIButton, UIDiv, UIText } from '@ui';
import { getCadFileModelId, getReconstructionId } from '@utils/cadFileModel.js';
import { S3_BUCKET } from '/environment.js';

class AiAnalyzerFilesPopover extends UIDiv {
  constructor() {
    super();
    this.addClass('w-96 h-[450px] rounded-lg overflow-hidden flex flex-col');
    this.searchTerm = '';
    this.isBuilding = false;
    this.maxFiles = 10; // File selection limit

    this.activeTab = 'files'; // Start with files tab
    this.selectedTaskProjects = null; // Track selected task projects from viewer page
    this.projects = ProjectsState.projects;

    // Subscribe to state changes
    FoldersState.subscribeToUI(() => {
      this.#updateSelectionCounter();
      this.#updateDisabledStates();
      this.#buildUI();
    });
    ProjectsState.subscribe(this.#buildUI.bind(this));

    // Set initial projects and build UI
    this.projects = ProjectsState.projects;
    this.selectedTaskProjects = this.#getCurrentTaskFromUrl();
    if (this.selectedTaskProjects) {
      // If on viewer page, set active tab to current task
      this.activeTab = 'current';
    }
    this.#buildUI();
  }

  #getSelectedFilesCount() {
    let count = 0;
    FoldersState.folders.forEach(folder => {
      count += folder.urls.filter(url => url.selected).length;
    });
    return count;
  }

  #canSelectMoreFiles() {
    return this.#getSelectedFilesCount() < this.maxFiles;
  }

  #updateSelectionCounter() {
    // Find and update the selection counter in the header
    const selectedCount = this.#getSelectedFilesCount();
    const limitText = i18next.t('sidebar.aiAnalyzer.files_selected_limit');

    // Find the counter element and update it
    const counterElement = this.find('.selection-counter')[0];
    if (counterElement) {
      counterElement.setTextContent(limitText);
      counterElement.className = `selection-counter font-medium text-sm ${
        selectedCount >= this.maxFiles ? 'text-red-600' : 'text-slate-700'
      }`;
    }
  }

  #updateDisabledStates() {
    const canSelectMore = this.#canSelectMoreFiles();

    // Update all file items based on selection state
    const allFileItems = this.find('[id^="project-file-item-"]');
    allFileItems.forEach(fileItem => {
      const filePath = this.#getFilePathFromItemId(fileItem.dom.id);
      const taskId = this.#getTaskIdFromItemId(fileItem.dom.id);
      const isSelected = this.#isProjectFileSelected(filePath, taskId);

      if (!isSelected) {
        // Update visual state for unselected items
        if (canSelectMore) {
          fileItem.removeClass('opacity-50', 'cursor-not-allowed');
          fileItem.addClass('cursor-pointer');
          if (!fileItem.hasClass('bg-blue-50')) {
            fileItem.addClass('hover:bg-slate-50');
          }
        } else {
          fileItem.addClass('opacity-50 cursor-not-allowed');
          fileItem.removeClass('cursor-pointer', 'hover:bg-slate-50');
        }
      }
    });
  }

  // Helper method to extract file path from item ID
  #getFilePathFromItemId(itemId) {
    // Extract task ID and encoded path from the item ID
    const match = itemId.match(/project-file-item-(.+)-(.+)$/);
    if (match) {
      const encodedPath = match[2];
      try {
        const decodedPath = decodeURIComponent(encodedPath);
        return S3_BUCKET + decodedPath;
      } catch (error) {
        console.error('Failed to decode file path from item ID:', error);
        return null;
      }
    }
    return null;
  }

  // Helper method to extract task ID from item ID
  #getTaskIdFromItemId(itemId) {
    const match = itemId.match(/project-file-item-(.+)-(.+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  // Helper method to check if we're on the viewer page
  #isOnViewerPage() {
    return window.location.pathname === '/viewer';
  }

  // Helper method to get current task data from URL params
  #getCurrentTaskFromUrl() {
    if (!this.#isOnViewerPage()) return null;

    const urlParams = new URLSearchParams(window.location.search);
    const sharingId = urlParams.get('sharingId');

    if (!sharingId) return null;

    // Try to find the task in personal projects first
    let currentTask = null;

    if (this.projects?.tasks) {
      currentTask = this.projects.tasks.find(
        task => task.sharingId === sharingId,
      );
    }

    // Return an object with the task as a child, similar to other project objects
    if (currentTask) {
      return {
        folders: [],
        tasks: [currentTask],
        rootFolderId: currentTask.parentId,
        isLoading: false,
      };
    }

    return null;
  }

  #buildUI() {
    if (this.isBuilding) return;
    this.isBuilding = true;

    // Update projects reference from state
    this.projects = ProjectsState.projects;

    this.clear();

    // Header with tabs and search
    const header = new UIDiv();
    header.addClass(
      'sticky top-0 bg-white border-b border-gray-200 p-2 flex-shrink-0',
    );

    // Tabs container
    const tabsContainer = new UIDiv();
    tabsContainer.addClass('flex mb-2 border-b border-gray-100');
    const isOnViewer = this.#isOnViewerPage();

    const tabConfigs = [
      {
        key: 'current',
        label: i18next.t('common.current_task'),
        hide: !isOnViewer || !this.selectedTaskProjects,
      },
      {
        key: 'files',
        label: i18next.t('sidebar.aiAnalyzer.tabs.files'),
        hide: !isOnViewer || !this.selectedTaskProjects, // now that we only have two tabs, it is not needed to show only one so show it only when the other condition is met
      },
    ];

    const tabBaseClasses =
      'w-full px-3 py-2 text-sm font-medium border-b-2 transition-colors';

    tabConfigs.forEach(({ key, label, hide }) => {
      const isActive = this.activeTab === key;
      let stateClasses;

      if (isActive && !hide) {
        stateClasses = 'border-blue-500 text-blue-600';
      } else if (!hide) {
        stateClasses = 'border-transparent hover:bg-slate-50';
      } else {
        stateClasses = 'hidden';
      }

      const tab = new UIButton(label);
      tab.addClass(`${tabBaseClasses} ${stateClasses}`);

      if (!hide) {
        tab.onClick(e => {
          e.stopPropagation();
          this.activeTab = key;
          this.#buildUI();
        });
      } else {
        tab.setDisabled(true);
      }

      tabsContainer.add(tab);
    });

    header.add(tabsContainer);

    const search = new Search({
      handleSearch: searchValue => {
        this.searchTerm = searchValue.toLowerCase();
        if (this.activeTab === 'files') {
          this.#buildFilesList();
        }
      },
      placeholder: `${i18next.t('sidebar.aiAnalyzer.search_pdf')}...`,
    });

    header.add(search);

    this.add(header);

    // Files list container with scrolling
    this.listContainer = new UIDiv();
    this.listContainer.addClass('p-2 space-y-2 overflow-y-auto flex-1');
    this.add(this.listContainer);

    this.#buildFilesList();
    this.isBuilding = false;
  }

  #buildFilesList() {
    this.listContainer.clear();
    // Get projects based on active tab from local storage
    let projects;
    switch (this.activeTab) {
      case 'current':
        projects = this.selectedTaskProjects;
        break;
      case 'files':
        projects = this.projects;
        break;
      default:
        projects = null;
        break;
    }
    // Check if projects are still loading or not available
    if (!projects || projects.isLoading) {
      const loadingSection = new UIDiv();
      loadingSection.addClass('space-y-1 mb-4');
      const loadingMessage = new UIText(i18next.t('common.loading'));
      loadingMessage.addClass('text-sm text-slate-500 px-2 py-1');
      loadingSection.add(loadingMessage);

      this.listContainer.add(loadingSection);
      return;
    }

    const tasks = projects?.tasks || [];

    // Extract all PDF files from tasks
    const pdfFiles = [];
    tasks.forEach(task => {
      if (task.reconstructions && task.reconstructions.length > 0) {
        task.reconstructions
          .filter(reconstruction => reconstruction.filePath?.endsWith('.pdf'))
          .forEach(reconstruction => {
            pdfFiles.push({
              taskName: task.name,
              taskId: task.id,
              cadFileModelId: getCadFileModelId(reconstruction),
              reconstructionId: getReconstructionId(reconstruction),
              path: reconstruction.filePath,
              originalName:
                reconstruction.fileOriginalName ||
                reconstruction.filePath.split('/').pop(),
              fileSize: reconstruction.fileSize,
              uniqueId: `${task.id}-${reconstruction.filePath}`, // Unique identifier for this specific file instance
              selected: this.#isProjectFileSelected(
                reconstruction.filePath,
                task.id,
              ),
            });
          });
      }
    });

    // Filter by search term
    const filteredPdfs = pdfFiles.filter(
      file =>
        file.originalName.toLowerCase().includes(this.searchTerm) ||
        file.taskName.toLowerCase().includes(this.searchTerm),
    );

    if (filteredPdfs.length > 0) {
      const projectSection = new UIDiv();
      projectSection.addClass('space-y-1 mb-4');

      // Project section header
      const projectHeader = new UIDiv();
      projectHeader.addClass(
        'flex items-center justify-between pb-2 border-b border-gray-100',
      );

      // Selection limit indicator
      const selectedCount = this.#getSelectedFilesCount();
      const limitText = i18next.t('sidebar.aiAnalyzer.files_selected_limit');
      const limitIndicator = new UIText(limitText);
      limitIndicator.addClass(
        `selection-counter font-medium text-sm ${
          selectedCount >= this.maxFiles ? 'text-red-600' : 'text-slate-700'
        }`,
      );
      projectHeader.add(limitIndicator);

      const deselectAllProjectBtn = new UIButton(
        i18next.t('sidebar.aiAnalyzer.deselect_all'),
      );
      deselectAllProjectBtn.addClass(
        'text-sm text-slate-500 hover:text-slate-700 px-1.5 py-1 hover:bg-slate-100 rounded-md transition-colors',
      );
      deselectAllProjectBtn.onClick(e => {
        e.stopPropagation();
        this.#deselectAllProjectFiles(filteredPdfs, false);
      });
      projectHeader.add(deselectAllProjectBtn);

      projectSection.add(projectHeader);

      // Project PDF files
      filteredPdfs.forEach(pdfFile => {
        const fileItem = new UIDiv();
        // Use encodeURIComponent to make sure the ID is valid
        const encodedPath = encodeURIComponent(
          pdfFile.path.replace(S3_BUCKET, ''),
        ).replace(/%2F/g, '-'); // Replace slashes for valid ID
        fileItem.setId(`project-file-item-${pdfFile.taskId}-${encodedPath}`);

        // Add visual indication based on selection state and availability
        const canSelect = pdfFile.selected || this.#canSelectMoreFiles();
        const isSelected = pdfFile.selected;

        let baseClasses =
          'flex items-center gap-1 p-2 py-2.5 rounded cursor-pointer transition-all duration-200';

        if (!canSelect && !isSelected) {
          baseClasses += ' opacity-50 cursor-not-allowed';
        } else if (isSelected) {
          // Selected state with your requested styling
          baseClasses +=
            ' border bg-[var(--o-hive-primary-20)] hover:bg-[var(--o-hive-primary-40)]';
        } else {
          baseClasses +=
            ' hover:bg-slate-50 border border-transparent hover:border-slate-300';
        }

        fileItem.addClass(baseClasses);

        // Add click handler for selection
        if (canSelect || isSelected) {
          fileItem.onClick(e => {
            e.stopPropagation();
            if (isSelected) {
              // Deselect the file
              this.#removeProjectFileFromFoldersState(pdfFile);
              // Update visual state to unselected
              fileItem.removeClass('bg-none');
              fileItem.addClass(
                'hover:bg-slate-50 border-transparent hover:border-slate-300',
              );
              // Remove selection indicator
              const indicator = fileItem.find(
                '.selection-indicator-' + pdfFile.taskId + '-' + encodedPath,
              )[0];
              if (indicator) {
                indicator.remove();
              }
            } else {
              // Select the file if we can select more
              if (this.#canSelectMoreFiles()) {
                this.#addProjectFileToFoldersState(pdfFile);
                // Update visual state to selected
                fileItem.removeClass(
                  'bg-none',
                  'hover:bg-slate-50',
                  'border-transparent',
                  'hover:border-slate-300',
                );
                fileItem.addClass(
                  'bg-[var(--o-hive-primary-20)] hover:bg-[var(--o-hive-primary-40)]',
                );
                // Add selection indicator
                const selectionIndicatorIcon = new CheckIcon({
                  color: 'none',
                  strokeColor: 'var(--o-hive-primary-100)',
                  size: 16,
                });
                selectionIndicatorIcon.addClass(
                  'selection-indicator-' + pdfFile.taskId + '-' + encodedPath,
                );
                fileItem.add(selectionIndicatorIcon);
              }
            }
            // Update counter and disabled states
            this.#updateSelectionCounter();
            this.#updateDisabledStates();
          });
        }

        const fileInfo = new UIDiv();
        fileInfo.addClass('flex-1 min-w-0');

        const fileName = new UIText(pdfFile.originalName);
        fileName.addClass('text-sm text-slate-700 truncate block');
        fileInfo.add(fileName);

        const taskName = new UIText(`from: ${pdfFile.taskName}`);
        taskName.addClass('text-xs text-slate-500 truncate block');
        fileInfo.add(taskName);

        fileItem.add(fileInfo);

        // Add selection indicator when file is selected
        if (isSelected) {
          const selectionIndicatorIcon = new CheckIcon({
            color: 'none',
            size: 16,
            strokeColor: 'var(--o-hive-primary-100)',
          });
          selectionIndicatorIcon.setClass(
            `selection-indicator-${pdfFile.taskId}-${encodedPath}`,
          );
          fileItem.add(selectionIndicatorIcon);
        }

        projectSection.add(fileItem);
      });

      this.listContainer.add(projectSection);
    } else if (filteredPdfs.length === 0 && tasks.length > 0) {
      // Show no results message if search didn't find anything
      const noResultsSection = new UIDiv();
      noResultsSection.addClass('space-y-1 mb-4');
      const noResultsMessage = new UIText(
        i18next.t('sidebar.aiAnalyzer.no_pdf_found'),
      );
      noResultsMessage.addClass('text-sm text-slate-500 px-2 py-1');
      noResultsSection.add(noResultsMessage);

      this.listContainer.add(noResultsSection);
    } else {
      // Show no PDF files message
      const noPdfsSection = new UIDiv();
      noPdfsSection.addClass('space-y-1 mb-4');
      const noPdfsMessage = new UIText(
        i18next.t('sidebar.aiAnalyzer.no_pdf_projects', {
          activeTab: i18next.t('common.my_projects'),
        }),
      );
      noPdfsMessage.addClass('text-sm text-slate-500 px-2 py-1');
      noPdfsSection.add(noPdfsMessage);

      this.listContainer.add(noPdfsSection);
    }
  }

  #deselectAllProjectFiles(files) {
    files.forEach(file => {
      this.#removeProjectFileFromFoldersState(file);

      // Update visual state of file item using getElementById with properly encoded path
      const encodedPath = encodeURIComponent(
        file.path.replace(S3_BUCKET, ''),
      ).replace(/%2F/g, '-'); // Replace slashes for valid ID
      const fileItemId = `project-file-item-${file.taskId}-${encodedPath}`;
      const fileElement = document.getElementById(fileItemId);
      if (fileElement) {
        fileElement.classList.remove(
          'bg-[var(--o-hive-primary-20)]',
          'hover:bg-[var(--o-hive-primary-40)]',
        );
        fileElement.classList.add(
          'bg-none',
          'hover:bg-slate-50',
          'border-transparent',
          'hover:border-slate-300',
        );
        // Remove selection indicator
        const indicator = fileElement.querySelector(
          '.selection-indicator-' + file.taskId + '-' + encodedPath,
        );
        if (indicator) {
          indicator.remove();
        }
      }
    });

    // Update counter and disabled states without full rebuild
    this.#updateSelectionCounter();
    this.#updateDisabledStates();
  }

  #addProjectFileToFoldersState(pdfFile) {
    const folderName = pdfFile.taskName;
    const existingFolder = FoldersState.folders.find(
      f => f.name === folderName,
    );

    if (existingFolder) {
      // Check if this specific file instance already exists (by path AND taskId)
      const fileExists = existingFolder.urls.some(
        url => url.path === pdfFile.path && url.taskId === pdfFile.taskId,
      );
      if (!fileExists) {
        existingFolder.urls.push({
          path: pdfFile.path,
          originalName: pdfFile.originalName,
          fileSize: pdfFile.fileSize,
          taskId: pdfFile.taskId, // Store taskId to distinguish file instances
          cadFileModelId: pdfFile.cadFileModelId,
          reconstructionId: pdfFile.reconstructionId,
          selected: true,
        });
        FoldersState.notifyUIListeners();
      }
    } else {
      // Create new folder
      const newFolder = {
        name: folderName,
        urls: [
          {
            path: pdfFile.path,
            originalName: pdfFile.originalName,
            fileSize: pdfFile.fileSize,
            taskId: pdfFile.taskId, // Store taskId to distinguish file instances
            cadFileModelId: pdfFile.cadFileModelId,
            reconstructionId: pdfFile.reconstructionId,
            selected: true,
          },
        ],
      };
      FoldersState.addFolder(newFolder);
    }
  }

  #removeProjectFileFromFoldersState(pdfFile) {
    const folderName = pdfFile.taskName;
    const existingFolder = FoldersState.folders.find(
      f => f.name === folderName,
    );

    if (existingFolder) {
      // Remove only the specific file instance (by path AND taskId)
      existingFolder.urls = existingFolder.urls.filter(
        url => !(url.path === pdfFile.path && url.taskId === pdfFile.taskId),
      );
      if (existingFolder.urls.length === 0) {
        FoldersState.removeFolder(folderName);
      } else {
        FoldersState.notifyUIListeners();
      }
    }
  }

  #isProjectFileSelected(filePath, taskId = null) {
    // Check if this specific project file instance is already selected in FoldersState
    return FoldersState.folders.some(folder =>
      folder.urls.some(url => {
        // If taskId is provided, check for exact match including task context
        if (taskId && url.taskId) {
          return url.path === filePath && url.taskId === taskId && url.selected;
        }
        // Fallback to path-only check for backwards compatibility
        return url.path === filePath && url.selected;
      }),
    );
  }
}

export { AiAnalyzerFilesPopover };
